import moment, { type Moment } from 'moment'

import type {
  InventoryOrder,
  InventoryOrderItem,
  InventoryOrderItemReceipt,
} from '~/api/graphql/generated/types'
import type {
  InventoryOrderForTable,
  InventoryOrderItemForTable,
} from '~/types'
import { getInventoryItemDisplayName } from '~/utils/inventoryItemUtils'

const STATUS_RECEIVED = 'RECEIVED'

const getDerivedInventoryOrderValues = (items: InventoryOrderItem[]) => {
  const categories = new Set<string>()
  const inventoryOrderFlags = { controlled: false, storeCold: false }
  const receivedDates: Moment[] = []
  let linesReceived = 0

  items.forEach((item) => {
    // @ts-expect-error This doesn't exist on the schema yet, but will be added
    inventoryOrderFlags.storeCold ||= item.inventoryItem.storeCold
    inventoryOrderFlags.controlled ||= item.inventoryItem.controlled

    categories.add(item.inventoryItem.category.id)

    if (item.status.name.toUpperCase() === STATUS_RECEIVED) {
      linesReceived++
    }

    receivedDates.push(
      ...item.receipts
        .filter(({ creationDate }) => Boolean(creationDate))
        .map(({ creationDate }) => moment(creationDate)),
    )
  })

  return {
    categories: Array.from(categories),
    inventoryOrderFlags,
    lastReceivedDate: receivedDates.length
      ? moment.max(receivedDates).toISOString()
      : '',
    linesReceived,
  }
}

export const transformInventoryOrderForTable = (
  order: InventoryOrder,
): InventoryOrderForTable => ({
  id: order.id,
  orderNumber: order.orderNumber,
  statusId: order.status.id,
  distributorId: order.distributor.id,
  distributorName: order.distributor.name,
  orderedByEmail: order.externalCreatorEmail || '',
  orderedByFirstName: order.externalCreatorFirstName || '',
  orderedByLastName: order.externalCreatorLastName || '',
  orderedDate: order.orderedDate,
  totalLines: order.items.length,
  ...getDerivedInventoryOrderValues(order.items),
})

const getReceivedQuantities = (receipts: InventoryOrderItemReceipt[]) =>
  receipts.reduce(
    (acc, receipt) => {
      if (receipt.damageReason) {
        acc.damagedQuantity += receipt.receivedQuantity
      } else {
        acc.receivedQuantity += receipt.receivedQuantity
      }
      return acc
    },
    { receivedQuantity: 0, damagedQuantity: 0 },
  )

export const transformInventoryOrderItemForTable = (
  item: InventoryOrderItem,
): InventoryOrderItemForTable => ({
  id: item.id,
  cumulusId: item.inventoryItem.variation?.cumulusId || '',
  name: getInventoryItemDisplayName(item.inventoryItem),
  notes: item.notes || '',
  orderedQuantity: item.orderedQuantity,
  packageTypeId: item.packageType.id,
  packageUnitsId: item.packageUnits.id,
  perPackageAmount: item.perPackageAmount,
  sku: item.sku || '',
  statusId: item.status.id,
  // @ts-expect-error This value has not yet been added to the GraphQL type but will be
  storeCold: item.inventoryItem.storeCold,
  controlled: item.inventoryItem.controlled,
  receipts: item.receipts,
  ...getReceivedQuantities(item.receipts),
})
